/**
 * Date wording, Canadian style: day name first, then day and month ("Mon 11
 * May"). Pure, so the server (CSV) and the browser (pages) say the same
 * thing. No Intl: the wording must not depend on the machine's locale data.
 */
import { addDays, dayOfWeek, daysBetween, yearOf, type ISODate } from '../dates/iso.ts';
import type { Week } from '../weeks/weeks.ts';

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function dayNumber(date: ISODate): number {
  return Number(date.slice(8, 10));
}

function monthName(date: ISODate): string {
  return MONTH_NAMES[Number(date.slice(5, 7)) - 1] ?? '';
}

/** "Mon 11 May" */
export function formatDay(date: ISODate): string {
  return `${DAY_NAMES[dayOfWeek(date)] ?? ''} ${String(dayNumber(date))} ${monthName(date)}`;
}

/** "Mon 11 May 2026" */
export function formatDayYear(date: ISODate): string {
  return `${formatDay(date)} ${String(yearOf(date))}`;
}

/** "11 May 2026" */
export function formatDate(date: ISODate): string {
  return `${String(dayNumber(date))} ${monthName(date)} ${String(yearOf(date))}`;
}

/**
 * "Mon 11 May – Mon 18 May", with years added when they differ:
 * "Mon 28 Dec 2026 – Sun 3 Jan 2027".
 */
export function formatRange(start: ISODate, end: ISODate): string {
  if (yearOf(start) === yearOf(end)) return `${formatDay(start)} – ${formatDay(end)}`;
  return `${formatDayYear(start)} – ${formatDayYear(end)}`;
}

/** "8 Jun – 6 Dec 2026" or "8 Jun 2026 – 7 Mar 2027", for a schedule's period. */
export function formatPeriod(start: ISODate, end: ISODate): string {
  const s = `${String(dayNumber(start))} ${monthName(start)}`;
  if (yearOf(start) === yearOf(end)) return `${s} – ${formatDate(end)}`;
  return `${s} ${String(yearOf(start))} – ${formatDate(end)}`;
}

/** The last calendar day of a Monday-to-Sunday week. */
export function sundayOf(weekStart: ISODate): ISODate {
  return addDays(weekStart, 6);
}

/**
 * "7 days", "8 days · covers Victoria Day", "6 days · starts Tuesday after
 * Victoria Day".
 */
export function describeDays(week: Pick<Week, 'onCallStart' | 'onCallEnd' | 'coveredMonday' | 'skippedMonday'>): string {
  const days = daysBetween(week.onCallStart, week.onCallEnd) + 1;
  const base = `${String(days)} days`;
  if (week.coveredMonday) return `${base} · covers ${week.coveredMonday.holiday}`;
  if (week.skippedMonday) return `${base} · starts Tuesday after ${week.skippedMonday.holiday}`;
  return base;
}

export function kindLabel(kind: Week['kind']): string {
  switch (kind) {
    case 'long-weekend':
      return 'Long weekend';
    case 'christmas':
      return 'Christmas week';
    case 'new-year':
      return 'New Year week';
    case 'regular':
      return 'Regular';
  }
}
